import { t } from '@/i18next';

import type { ParseKeys } from 'i18next';

// Table layout
export const DEFAULT_COL_SPAN = 1;
export const DEFAULT_PAGE_INDEX = 1;
export const DEFAULT_ROW_SPAN = 1;
export const SIBLING_COUNT = 2;
export const DEFAULT_COLUMN_MAX_SIZE = 50;

export const DISABLED_VIEW_ACTION_LIST = [
  'actions',
  'select',
  'active',
  'isEnabled'
] as const;

// Pagination
export const PER_PAGE_DEFAULT = 10;
export const PER_PAGE = [PER_PAGE_DEFAULT, 25, 50, 100, 500] as const;

// List content
export const ACCOUNTS_LIST = 'accounts';
export const GROUPS_LIST = 'groups';
export const TOKENS_LIST = 'tokens';

// Custom table constants
export const FULLNAME_COLUMN = 'fullName';
export const EMAIL_COLUMN = 'email';
export const GROUPS_COLUMN = 'groups';
export const LOGIN_TYPE = 'loginType';
export const ROLES_COLUMN = 'roles';
export const USER_TYPE_COLUMN = 'userType';
export const NAME_COLUMN = 'name';
export const DESCRIPTION_COLUMN = 'description';
export const PRODUCT_ACCESS_COLUMN = 'productAccess';
export const NAMESPACE_COLUMN = 'namespace';
export const DOCUMENTS_COUNT_COLUMN = 'documentsCount';
export const CREATED_AT_COLUMN = 'createdAt';
export const CONTAINER_OWNER_COLUMN = 'ownerName';
export const CONTAINER_ACCESS_LEVEL_COLUMN = 'accessLevel';
export const SELECT_COLUMN = 'select';

export const DEFAULT_RENDERED_ROWS = [0, 1, 2, 3] as const;

export const EXTENDED_COLUMNS = [GROUPS_COLUMN] as const;

export const SORTABLE_COLUMNS = [NAME_COLUMN] as const;

export function createColumn(
  key: AllowedColumns,
  label?: ParseKeys,
  tooltip?: ParseKeys
) {
  return {
    key: key,
    label: label ? t(label) : '',
    tooltip: tooltip ? t(tooltip) : ''
  } as const;
}

export const COLUMNS_MAP = {
  [CREATED_AT_COLUMN]: () => createColumn(CREATED_AT_COLUMN, 'manageAccounts.created_at'),
  [CONTAINER_ACCESS_LEVEL_COLUMN]: () => createColumn(CONTAINER_ACCESS_LEVEL_COLUMN, 'container.access_level'),
  [CONTAINER_OWNER_COLUMN]: () => createColumn(CONTAINER_OWNER_COLUMN, 'container.owner'),
  [DESCRIPTION_COLUMN]: () => createColumn(DESCRIPTION_COLUMN, 'manageGroups.description'),
  [DOCUMENTS_COUNT_COLUMN]: () => createColumn(DOCUMENTS_COUNT_COLUMN, 'container.documents_count'),
  [EMAIL_COLUMN]: () => createColumn(EMAIL_COLUMN, 'manageAccounts.email'),
  [FULLNAME_COLUMN]: () => createColumn(FULLNAME_COLUMN, 'common.name'),
  [GROUPS_COLUMN]: () => createColumn(GROUPS_COLUMN, 'modals.assigned_groups'),
  [LOGIN_TYPE]: () => createColumn(LOGIN_TYPE, 'modals.login_method'),
  [NAME_COLUMN]: () => createColumn(NAME_COLUMN, 'common.name', 'manageGroups.sortByName'),
  [NAMESPACE_COLUMN]: () => createColumn(NAMESPACE_COLUMN, 'container.namespace'),
  [PRODUCT_ACCESS_COLUMN]: () => createColumn(PRODUCT_ACCESS_COLUMN, 'manageAccounts.productAccess'),
  [ROLES_COLUMN]: () => createColumn(ROLES_COLUMN, 'manageAccounts.user_roles'),
  [SELECT_COLUMN]: () => createColumn(SELECT_COLUMN),
  [USER_TYPE_COLUMN]: () => createColumn(USER_TYPE_COLUMN, 'manageAccounts.patents_platform_user_type'),
} as const;

function isExtendedColumn(column: AllowedColumns) {
  return EXTENDED_COLUMNS.some(extendedColumn => extendedColumn === column);
}

function isSortableColumn(column: AllowedColumns) {
  return SORTABLE_COLUMNS.some(extendedColumn => extendedColumn === column);
}

export function createTableColumns(columns: AllowedColumns[]) {
  return columns.map(column => ({
    isExtended: isExtendedColumn(column),
    isSortable: isSortableColumn(column),
    value: COLUMNS_MAP[column]()
  }));
}

export function renderRow(cellData: unknown) {
  if (Array.isArray(cellData)) {
    return cellData.length ? cellData.join(', ') : t('common.none');
  }

  return (!cellData && cellData !== 0) ? t('common.none') : cellData;
}

export type AllowedColumns =
  | typeof CREATED_AT_COLUMN
  | typeof CONTAINER_OWNER_COLUMN
  | typeof CONTAINER_ACCESS_LEVEL_COLUMN
  | typeof DESCRIPTION_COLUMN
  | typeof DOCUMENTS_COUNT_COLUMN
  | typeof EMAIL_COLUMN
  | typeof FULLNAME_COLUMN
  | typeof GROUPS_COLUMN
  | typeof LOGIN_TYPE
  | typeof NAME_COLUMN
  | typeof NAMESPACE_COLUMN
  | typeof PRODUCT_ACCESS_COLUMN
  | typeof ROLES_COLUMN
  | typeof SELECT_COLUMN
  | typeof USER_TYPE_COLUMN;

export type CustomTableColumn = ReturnType<typeof createTableColumns>[number];
